import { useState, useEffect, useRef } from 'react'
import { ChevronDown } from 'lucide-react'
import { listCategories, type Category } from '../lib/api'
import { buildCategoryConfig, getCategoryColorClasses } from '../lib/categoryColors'

interface InlineCategoryPickerProps {
  currentCategory: string | null
  canEdit: boolean
  onCategoryChange: (category: string | null) => Promise<void>
}

export default function InlineCategoryPicker({
  currentCategory,
  canEdit,
  onCategoryChange,
}: InlineCategoryPickerProps) {
  const [categories, setCategories] = useState<Category[]>([])
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    listCategories().then(setCategories).catch(() => {})
  }, [])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const config = buildCategoryConfig(categories)
  const currentCfg = currentCategory ? config[currentCategory] : null

  async function handleSelect(slug: string | null) {
    if (slug === currentCategory) {
      setOpen(false)
      return
    }
    setSaving(true)
    try {
      await onCategoryChange(slug)
    } catch {
      // ignore
    } finally {
      setSaving(false)
      setOpen(false)
    }
  }

  // Read-only: just show the hashtag
  if (!canEdit) {
    if (!currentCfg) return null
    return (
      <span className={`text-xs ${currentCfg.text}`}>#{currentCfg.label}</span>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        disabled={saving}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors border ${
          currentCfg
            ? `${currentCfg.bg} ${currentCfg.text} border-transparent`
            : 'border-dashed border-border text-muted-foreground hover:border-primary/30'
        } ${saving ? 'opacity-50' : 'hover:bg-secondary/50'}`}
      >
        {currentCfg ? `#${currentCfg.label}` : 'Add category'}
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-20 py-1">
            <button
              onClick={() => handleSelect(null)}
              className={`w-full px-3 py-1.5 text-left text-sm hover:bg-secondary flex items-center gap-2 ${
                !currentCategory ? 'font-medium text-primary' : ''
              }`}
            >
              No category
            </button>
            {categories.map(cat => {
              const cfg = config[cat.slug]
              const colorClasses = getCategoryColorClasses(cat.color)
              return (
                <button
                  key={cat.slug}
                  onClick={() => handleSelect(cat.slug)}
                  className={`w-full px-3 py-1.5 text-left text-sm hover:bg-secondary flex items-center gap-2 ${
                    currentCategory === cat.slug ? 'font-medium' : ''
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${colorClasses.dot}`} />
                  <span className={currentCategory === cat.slug && cfg ? cfg.text : ''}>
                    {cat.name}
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
